/**
 * A/B test: a shortfall-sized ask at the Studio credit wall (web).
 *
 * Control keeps today's wall — the stepper tells a free user "you have N
 * credits" (no shortfall number) and the upgrade modal opens as a generic
 * plan list / pack grid with a static badge. Nothing on screen says what
 * would cover THIS video.
 *
 * Treatment ("ask") mirrors the mobile shortfall sheet (#703, #753): the
 * wall copy states the shortfall, the modal's header reads "this video needs
 * N more credits", and the cheapest plan (free users, monthly tab first) or
 * pack (subscribers, top-up tab) that covers the shortfall carries a
 * "Covers this video · X to spare" badge instead of the static one.
 *
 * Assignment is per USER on a salted FNV-1a bucket (the free_length_default_v1
 * scheme with its own salt, so the two tests split users independently) and
 * can be recomputed offline from users.id.
 *
 * Kill switch: VITE_SHORTFALL_ASK_PERCENT=0 (build-time), default 50.
 */

import { fnv1a32 } from "./directorStyleBoost";
import {
  CREDIT_PACK_ORDER,
  PRICING_PLANS,
  getPlanTierIndex,
  type CreditPackId,
  type PlanId,
} from "./pricingPlans";

export const SHORTFALL_ASK_EXPERIMENT = "shortfall_ask_v1";

export type ShortfallAskArm = "ask" | "control";

export function shortfallAskPercent(raw: unknown): number {
  if (raw === undefined || raw === null || raw === "") return 50;
  const value = Number(raw);
  if (!Number.isFinite(value)) return 50;
  return Math.min(100, Math.max(0, Math.round(value)));
}

/** Stable 0–99 bucket over the salted user id. */
export function shortfallAskBucket(userId: string): number {
  return fnv1a32(`${SHORTFALL_ASK_EXPERIMENT}:${userId}`) % 100;
}

export function shortfallAskArm(
  userId: string | number | null | undefined,
  percent: number,
): ShortfallAskArm | null {
  if (userId === null || userId === undefined || userId === "") return null;
  return shortfallAskBucket(String(userId)) < percent ? "ask" : "control";
}

export type ShortfallRecommendation =
  | { kind: "plan"; id: PlanId; spare: number }
  | { kind: "pack"; id: CreditPackId; spare: number };

type PlanLike = {
  id: PlanId;
  credits: number;
  monthlyPrice: number;
  creditsCadence?: "daily" | "monthly";
};

/**
 * The cheapest monthly plan whose monthly credits cover the shortfall, above
 * the user's current tier. Null when no plan covers it (the modal then falls
 * back to the covering pack) or when the shortfall is not a positive number.
 */
export function coveringPlan(
  shortfall: number,
  currentBasePlan: string,
  plans: PlanLike[] = PRICING_PLANS,
): ShortfallRecommendation | null {
  if (!(shortfall > 0)) return null;
  const currentTier = getPlanTierIndex(currentBasePlan);
  const candidates = plans
    .filter(
      (p) =>
        p.id !== "free" &&
        p.creditsCadence !== "daily" &&
        p.monthlyPrice > 0 &&
        getPlanTierIndex(p.id) > currentTier &&
        p.credits >= shortfall,
    )
    .sort((a, b) => a.monthlyPrice - b.monthlyPrice);
  const pick = candidates[0];
  return pick
    ? { kind: "plan", id: pick.id, spare: pick.credits - shortfall }
    : null;
}

type PackLike = { credits: number; price: number };

/**
 * The cheapest pack whose credits cover the shortfall; the largest pack when
 * none does (so the ask is always "the one that gets you closest"). Null
 * until the pack list has loaded or when the shortfall is not positive.
 */
export function coveringPack(
  shortfall: number,
  packs: Partial<Record<CreditPackId, PackLike>> | null | undefined,
  order: readonly CreditPackId[] = CREDIT_PACK_ORDER,
): ShortfallRecommendation | null {
  if (!(shortfall > 0) || !packs) return null;
  const loaded = order
    .map((id) => ({ id, pack: packs[id] }))
    .filter((x): x is { id: CreditPackId; pack: PackLike } => !!x.pack);
  if (!loaded.length) return null;
  const covering = loaded
    .filter((x) => x.pack.credits >= shortfall)
    .sort((a, b) => a.pack.price - b.pack.price)[0];
  const pick = covering ?? loaded[loaded.length - 1];
  return { kind: "pack", id: pick.id, spare: pick.pack.credits - shortfall };
}

/**
 * Which tab the sized ask opens on. Free users see the plans first with the
 * covering plan highlighted (the mobile sheet's "$35 Plus first" rule);
 * subscribers and pack buyers land on the pack grid. A shortfall no plan
 * covers also goes straight to packs.
 */
export function shortfallAskDefaultTab(input: {
  hasPaidAccess: boolean;
  currentBasePlan: string;
  shortfall: number;
}): "monthly" | "topup" {
  if (input.hasPaidAccess) return "topup";
  return coveringPlan(input.shortfall, input.currentBasePlan)
    ? "monthly"
    : "topup";
}

/** Analytics label for the recommended SKU, e.g. "plan:plus" / "pack:small". */
export function shortfallRecommendationLabel(
  rec: ShortfallRecommendation | null | undefined,
): string | undefined {
  return rec ? `${rec.kind}:${rec.id}` : undefined;
}
